import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { login, getMe, getToken } from "../api";

const CSS = `
  *, *::before, *::after { box-sizing: border-box; }

  .l-page {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    background-color: #f8fafc;
    font-family: system-ui, sans-serif;
  }

  .l-wrap {
    width: 100%;
    max-width: 380px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
  }

  .l-mark {
    display: flex;
    align-items: center;
    gap: 9px;
  }

  .l-mark-icon {
    width: 36px;
    height: 36px;
    background: #ede9fe;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #4f46e5;
    font-size: 13px;
    font-weight: 800;
    letter-spacing: -0.5px;
    flex-shrink: 0;
  }

  .l-mark-name {
    font-size: 20px;
    font-weight: 700;
    color: #4f46e5;
  }

  .l-card {
    width: 100%;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.08);
    padding: 36px 36px 32px;
  }

  .l-heading {
    font-size: 20px;
    font-weight: 700;
    color: #1e293b;
    margin: 0 0 4px;
  }

  .l-sub {
    font-size: 14px;
    color: #64748b;
    margin: 0 0 28px;
  }

  .l-error {
    padding: 10px 12px;
    background: #fee2e2;
    border-radius: 6px;
    color: #dc2626;
    font-size: 13px;
    margin-bottom: 18px;
    line-height: 1.4;
  }

  .l-field {
    display: flex;
    flex-direction: column;
    gap: 5px;
    margin-bottom: 14px;
  }

  .l-label {
    font-size: 13px;
    font-weight: 600;
    color: #374151;
  }

  .l-input {
    padding: 9px 12px;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    font-size: 14px;
    color: #1e293b;
    outline: none;
    width: 100%;
    font-family: inherit;
    transition: border-color 0.14s, box-shadow 0.14s;
    -webkit-appearance: none;
  }

  .l-input::placeholder { color: #94a3b8; }

  .l-input:focus {
    border-color: #4f46e5;
    box-shadow: 0 0 0 3px rgba(79,70,229,0.12);
  }

  .l-btn {
    margin-top: 8px;
    width: 100%;
    padding: 9px;
    background: #4f46e5;
    color: #fff;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
    transition: background 0.14s, opacity 0.14s;
  }

  .l-btn:hover:not(:disabled) { background: #4338ca; }
  .l-btn:disabled { opacity: 0.55; cursor: not-allowed; }
  .l-btn:focus-visible { outline: 2px solid #4f46e5; outline-offset: 2px; }

  .l-footer {
    font-size: 12px;
    color: #94a3b8;
    text-align: center;
  }
`;

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = getToken();
    if (!token) return;
    // Admins land on the same dashboard as everyone else and reach the admin
    // area from the "Admin Dashboard" link in its header, rather than being routed
    // away from their own apps on the way in.
    getMe()
      .then(() => navigate("/dashboard", { replace: true }))
      .catch(() => localStorage.removeItem("token"));
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await login(email, password);
      localStorage.setItem("token", data.access_token);
      await getMe();
      navigate("/dashboard");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <style>{CSS}</style>
      <div className="l-page">
        <div className="l-wrap">

          <div className="l-mark">
            <div className="l-mark-icon">AR</div>
            <span className="l-mark-name">AutoRev</span>
          </div>

          <div className="l-card">
            <h1 className="l-heading">Sign in</h1>
            <p className="l-sub">Manage your subscription payments</p>

            {error && (
              <div className="l-error">
                <span>⚠</span>
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="l-field">
                <label className="l-label" htmlFor="email">Email</label>
                <input
                  id="email"
                  className="l-input"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  autoComplete="email"
                />
              </div>

              <div className="l-field">
                <label className="l-label" htmlFor="password">Password</label>
                <input
                  id="password"
                  className="l-input"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
              </div>

              <button className="l-btn" type="submit" disabled={loading}>
                {loading ? "Signing in…" : "Sign in"}
              </button>
            </form>
          </div>

          <p className="l-footer">UPI Autopay subscription management</p>

        </div>
      </div>
    </>
  );
}
